import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { RiskBadge, StatusBadge } from "../components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = ["open", "in_progress", "review", "done"];

export default function TasksKanban({ embedded }) {
  const [tasks, setTasks] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", risk: "medium", status: "open", owner_name: "" });

  const load = async () => {
    const { data } = await api.get("/tasks", { params: { limit: 500 } });
    setTasks(Array.isArray(data) ? data : (data.items || []));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/tasks", form); toast.success("Task created"); setOpen(false); load(); }
    catch { toast.error("Create failed"); }
  };

  const move = async (task, status) => {
    await api.put(`/tasks/${task.id}`, { status });
    load();
  };

  if (!tasks) return <Loader />;

  return (
    <div>
      <PageHeader
        title={embedded ? "" : "Task Management"}
        description={embedded ? "" : "Kanban board of DPDP compliance tasks across the platform."}
        testId="tasks-page-header"
        actions={<Btn data-testid="task-create-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4" /> New Task</Btn>}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map((col) => (
          <div key={col} data-testid={`kanban-column-${col}`} className="bg-white border border-slate-200 rounded-md p-3 min-h-[60vh]">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center justify-between">
              <span>{col.replace(/_/g, " ")}</span>
              <span className="text-slate-400">{tasks.filter(t => t.status === col).length}</span>
            </div>
            <div className="space-y-2">
              {tasks.filter(t => t.status === col).map((t) => (
                <div key={t.id} className="border border-slate-200 rounded-md p-3 hover:border-slate-300 bg-white">
                  <div className="text-sm font-medium text-slate-900">{t.title}</div>
                  {t.description && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{t.description}</div>}
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    <RiskBadge level={t.risk || t.priority} />
                    <span className="text-[10px] text-slate-500">{t.due_date?.slice(0,10) || "—"}</span>
                  </div>
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {COLUMNS.filter(c => c !== col).map((c) => (
                      <button
                        key={c}
                        data-testid={`task-move-${t.id}-${c}`}
                        onClick={() => move(t, c)}
                        className="text-[10px] px-2 py-0.5 border border-slate-200 rounded hover:border-blue-300 hover:text-blue-700"
                      >
                        → {c.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Title" required className="sm:col-span-2"><TextInput data-testid="task-field-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></Field>
            <Field label="Description" className="sm:col-span-2"><TextArea data-testid="task-field-description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></Field>
            <Field label="Priority"><SelectInput value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})}>{["low","medium","high","critical"].map(o => <option key={o} value={o}>{o}</option>)}</SelectInput></Field>
            <Field label="Owner name"><TextInput value={form.owner_name} onChange={(e) => setForm({...form, owner_name: e.target.value})} /></Field>
            <Field label="Due date"><TextInput type="date" value={form.due_date || ""} onChange={(e) => setForm({...form, due_date: e.target.value ? new Date(e.target.value).toISOString() : null})} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn onClick={create} data-testid="task-save-btn">Create</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
